import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Truck, Plus, MapPin, Calendar, Clock, X, Users, Locate } from "lucide-react";

export default function VanSchedule() {
  const { user } = useAuth();
  const [vans, setVans] = useState([]);
  const [visits, setVisits] = useState([]);
  const [registering, setRegistering] = useState(false);
  const [scheduling, setScheduling] = useState(null); // {van}
  const [viewing, setViewing] = useState(null);       // {visit}

  const loadVans = () => axios.get(`${API}/partner/services/me`).then(r => setVans((r.data.items || []).filter(p => p.type === "mobile_vet_van")));
  const loadVisits = async () => {
    const ownedIds = new Set(vans.map(v => v.provider_id));
    if (ownedIds.size === 0) { setVisits([]); return; }
    const all = await axios.get(`${API}/mobile-van/visits`, { params: { upcoming_only: false } });
    setVisits(all.data.items.filter(v => ownedIds.has(v.van_id)));
  };
  useEffect(() => { loadVans(); }, []);
  useEffect(() => { loadVisits(); /* eslint-disable-next-line */ }, [vans.length]);

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="van-schedule-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><Truck className="w-4 h-4"/>Mobile Vet Van · Operator</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Your vans & visits</h1>
          <p className="text-ink-soft text-sm">Schedule community drives. Pet parents nearby get auto-notified.</p>
        </div>
        <button onClick={() => setRegistering(true)} data-testid="register-van-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1">
          <Plus className="w-4 h-4"/> Register a van
        </button>
      </header>

      <section className="space-y-3">
        <h2 className="font-heading text-xl font-bold">My vans</h2>
        {vans.length === 0 ? (
          <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No vans registered yet. Click "Register a van" — it takes 30 seconds.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vans.map(v => (
              <div key={v.provider_id} data-testid={`van-${v.provider_id}`} className="bg-surface border border-line rounded-3xl p-4">
                <div className="flex gap-3">
                  <img src={v.photo} alt="" className="w-16 h-16 rounded-2xl object-cover"/>
                  <div className="flex-1">
                    <div className="font-heading font-extrabold">{v.name}</div>
                    <div className="text-xs text-ink-soft mt-0.5">{v.city} · {v.van_registration_number || "—"}</div>
                    <div className="text-xs text-ink-soft mt-0.5">Vet: {v.vet_on_staff || "—"}</div>
                  </div>
                </div>
                <button onClick={() => setScheduling({ van: v })} data-testid={`schedule-${v.provider_id}`} className="mt-3 w-full bg-ink text-white rounded-full py-2 text-sm font-bold inline-flex items-center justify-center gap-1 hover:bg-brand">
                  <Calendar className="w-4 h-4"/> Schedule visit
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-xl font-bold">My visits</h2>
        {visits.length === 0 ? (
          <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No visits scheduled. Pick a neighbourhood and announce a drive.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visits.map(v => (
              <article key={v.visit_id} data-testid={`my-visit-${v.visit_id}`} className="bg-surface border border-line rounded-3xl p-4 shadow-soft">
                <div className="font-heading font-extrabold">{v.location_label}</div>
                <div className="text-xs text-ink-soft mt-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>{new Date(v.scheduled_at).toLocaleString()}</div>
                <div className="text-xs text-ink-soft inline-flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3"/>{v.city}</div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs font-bold inline-flex items-center gap-1"><Users className="w-3 h-3"/>{v.slots_booked}/{v.total_slots}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${v.status === "scheduled" ? "bg-leaf/40 text-ink" : "bg-surface-muted"}`}>{v.status}</span>
                </div>
                <button onClick={() => setViewing(v)} className="mt-2 w-full bg-surface-muted rounded-full py-1.5 text-xs font-bold">View bookings</button>
              </article>
            ))}
          </div>
        )}
      </section>

      {registering && <RegisterVanModal onClose={() => { setRegistering(false); loadVans(); }}/>}
      {scheduling && <ScheduleVisitModal van={scheduling.van} onClose={() => { setScheduling(null); loadVisits(); }}/>}
      {viewing && <VisitBookingsModal visit={viewing} onClose={() => setViewing(null)}/>}
    </div>
  );
}

function RegisterVanModal({ onClose }) {
  const [form, setForm] = useState({ name: "", city: "", phone: "", van_registration_number: "", vet_on_staff: "", services: ["vaccination", "deworming", "general-checkup"], lat: null, lng: null });
  const submit = async (e) => { e.preventDefault(); await axios.post(`${API}/mobile-van/register`, form); onClose(); };
  const useLocation = () => navigator.geolocation?.getCurrentPosition(
    p => setForm(f => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude })),
    () => alert("Could not get location"));
  return (
    <Modal onClose={onClose} title="Register a Mobile Vet Van">
      <form onSubmit={submit} className="space-y-3" data-testid="register-van-form">
        <F label="Van name *"><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="li" placeholder="PawCare Mobile Vet Van" data-testid="van-name"/></F>
        <F label="Base city *"><input required value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="li" placeholder="Noida"/></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Registration #"><input value={form.van_registration_number} onChange={e => setForm({ ...form, van_registration_number: e.target.value })} className="li" placeholder="KA-01-VN-2024"/></F>
          <F label="Vet on staff"><input value={form.vet_on_staff} onChange={e => setForm({ ...form, vet_on_staff: e.target.value })} className="li" placeholder="Dr. Anika Reddy"/></F>
        </div>
        <F label="Phone"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="li" placeholder="+91-…"/></F>
        <button type="button" onClick={useLocation} className="w-full bg-surface-muted rounded-full py-2 text-xs font-bold inline-flex items-center justify-center gap-1"><Locate className="w-3 h-3"/>{form.lat ? `Located (${form.lat.toFixed(3)}, ${form.lng.toFixed(3)})` : "Use my current location"}</button>
        <button data-testid="van-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold">Register van</button>
      </form>
    </Modal>
  );
}

function ScheduleVisitModal({ van, onClose }) {
  const [form, setForm] = useState({ van_id: van.provider_id, scheduled_at: "", duration_hours: 4, lat: van.lat || 0, lng: van.lng || 0, location_label: "", city: van.city, total_slots: 15, services_offered: ["vaccination", "deworming"], slot_fee: 300, notes: "" });
  const submit = async (e) => { e.preventDefault(); await axios.post(`${API}/mobile-van/visits`, form); onClose(); };
  const useLocation = () => navigator.geolocation?.getCurrentPosition(
    p => setForm(f => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude })),
    () => alert("Could not get location"));
  return (
    <Modal onClose={onClose} title={`Schedule visit · ${van.name}`}>
      <form onSubmit={submit} className="space-y-3" data-testid="schedule-form">
        <F label="Location label *"><input required value={form.location_label} onChange={e => setForm({ ...form, location_label: e.target.value })} className="li" placeholder="Indiranagar 12th Main Park" data-testid="vs-location"/></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Date & time *"><input required type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="li" data-testid="vs-when"/></F>
          <F label="Duration (hrs)"><input type="number" min="1" value={form.duration_hours} onChange={e => setForm({ ...form, duration_hours: Number(e.target.value) })} className="li"/></F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Latitude *"><input required type="number" step="any" value={form.lat || ""} onChange={e => setForm({ ...form, lat: Number(e.target.value) })} className="li"/></F>
          <F label="Longitude *"><input required type="number" step="any" value={form.lng || ""} onChange={e => setForm({ ...form, lng: Number(e.target.value) })} className="li"/></F>
        </div>
        <button type="button" onClick={useLocation} className="w-full bg-surface-muted rounded-full py-2 text-xs font-bold inline-flex items-center justify-center gap-1"><Locate className="w-3 h-3"/>Use my current location</button>
        <div className="grid grid-cols-2 gap-3">
          <F label="Total slots"><input type="number" min="1" value={form.total_slots} onChange={e => setForm({ ...form, total_slots: Number(e.target.value) })} className="li"/></F>
          <F label="Fee per slot (₹)"><input type="number" min="0" value={form.slot_fee} onChange={e => setForm({ ...form, slot_fee: Number(e.target.value) })} className="li"/></F>
        </div>
        <F label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="li" placeholder="Walk-ins welcome..."/></F>
        <button data-testid="vs-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold">Publish visit</button>
        <p className="text-xs text-ink-soft text-center">📍 Pet parents within 15 km radius will be notified automatically.</p>
      </form>
    </Modal>
  );
}

function VisitBookingsModal({ visit, onClose }) {
  const [bookings, setBookings] = useState([]);
  useEffect(() => { axios.get(`${API}/mobile-van/visits/${visit.visit_id}/bookings`).then(r => setBookings(r.data.items)); }, [visit.visit_id]);
  return (
    <Modal onClose={onClose} title={`Bookings · ${visit.location_label}`}>
      {bookings.length === 0 ? (
        <p className="text-ink-soft text-sm">No bookings yet for this visit.</p>
      ) : (
        <div className="divide-y divide-line max-h-[60vh] overflow-y-auto">
          {bookings.map(b => (
            <div key={b.slot_booking_id} className="py-2.5">
              <div className="font-bold">{b.pet_name} <span className="text-ink-soft font-normal text-sm">· {b.service_needed}</span></div>
              <div className="text-xs text-ink-soft">By {b.user_name} · ₹{b.amount}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        {children}
        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
